import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Chip,
  Alert,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ArrowLeft } from "lucide-react";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { EditIcon, ShareIcon } from "../comps/CustomIcons";
import { format } from "date-fns";
import { toast } from "react-toastify";

// Components
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";

// Services - following standard project pattern
import { 
  getExpenseReportApi
} from "../services/expenseReport.service";
import { exportExpenseReportApi, exportExpenseReportPDF } from "../services/reports.service";
import { getUserApi } from "../services/user.service";
import { getItemApi } from "../services/item.service";
import { getUnitApi } from "../services/unit.service";
import { getPatientApi } from "../services/patient.service";
import { getCategoryApi } from "../services/category.service";

// Hooks
import { useGetApi } from "../hooks/useGetApi";

// Dropdown Options
const visitTypeOptions = [
  { label: "OPD", value: "OPD" },
  { label: "IPD", value: "IPD" },
  { label: "Outsider", value: "Outsider" },
];

const expenseTypeOptions = [
  { label: "Service", value: "service" },
  { label: "Item", value: "item" },
  { label: "Procedure", value: "procedure" },
  { label: "Consultation", value: "consultation" },
  { label: "Room Charges", value: "room" },
];

const billStatusOptions = [
  { label: "Open", value: "open" },
  { label: "Closed", value: "closed" },
  { label: "Final Bill Initiated", value: "finalBillInitiated" },
  { label: "Final Bill Generated", value: "finalBillGenerated" },
  { label: "Reopen", value: "reopen" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Pending", value: "pending" },
];

const templateOptions = [
  { label: "All Expenses Template", value: "allExpenses" },
  { label: "OPD Expenses Template", value: "opdExpenses" },
  { label: "IPD Expenses Template", value: "ipdExpenses" },
  { label: "Service Expenses Template", value: "serviceExpenses" },
  { label: "Procedure Expenses Template", value: "procedureExpenses" },
];

const expenseStatusOptions = [
  { label: "Active", value: "active" },
  { label: "Completed", value: "completed" },
  { label: "Pending", value: "pending" },
  { label: "Cancelled", value: "cancelled" },
  { label: "In Progress", value: "inProgress" },
];

const menuItemStyle = {
  fontSize: "16px",
  fontWeight: "500",
  padding: "12px 16px",
};

const iconStyleProps = {
  backgroundColor: "rgba(240, 247, 252, 1)",
  width: "44px",
  height: "44px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};

const menuItems = [
  "Excel",
  "PDF",
  "Report Mail Send Scheduler",
  "Report Scheduler",
];



export default function ReportsExpense() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  // State for form data
  const [formData, setFormData] = useState({
    startDate: null,
    endDate: null,
    searchTemplate: null,
    category: null,
    subCategory: null,
    patientUhid: '',
    item: null,
    visitType: null,
    doctor: null,
    expenseType: null,
    billStatus: null,
    expenseStatus: null,
    unit: null,
  });

  // State for loading and errors
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState([]); // For required field validation errors
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    uhid: true,
    patientName: true,
    visitDate: true,
    visitType: true,
    itemService: true,
    quantity: true,
    amount: true,
  });

  // Menu anchor states
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const filterMenuOpen = Boolean(filterAnchorEl);
  const downloadMenuOpen = Boolean(downloadAnchorEl);
  
  // State for report data and pagination
  const [reportData, setReportData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 10,
    total: 0,
  });

  // Fetch dropdown data using standard useGetApi hook pattern - simplified
  const doctorApiResult = useGetApi({
    apiFunction: getUserApi,
    params: { limit: 1000 },
  });

  const itemApiResult = useGetApi({
    apiFunction: getItemApi,
    params: { limit: 1000 },
  });

  const unitApiResult = useGetApi({
    apiFunction: getUnitApi,
    params: { limit: 1000 },
  });

  const patientApiResult = useGetApi({
    apiFunction: getPatientApi,
    params: { limit: 1000 },
  });

  const categoryApiResult = useGetApi({
    apiFunction: getCategoryApi,
    params: { limit: 1000, type: "category" },
    dataKey: "categories",
  });

  const subCategoryApiResult = useGetApi({
    apiFunction: getCategoryApi,
    params: { limit: 1000, type: "subcategory" },
    dataKey: "categories",
  });

  // Extract data from results
  const doctorsData = doctorApiResult.dataList;
  const itemsData = itemApiResult.dataList;
  const unitsData = unitApiResult.dataList;
  const patientsData = patientApiResult.dataList;
  const categoriesData = categoryApiResult.dataList;
  const subCategoriesData = subCategoryApiResult.dataList;

  // Extract loading states
  const doctorsLoading = doctorApiResult.isLoading;
  const itemsLoading = itemApiResult.isLoading;
  const unitsLoading = unitApiResult.isLoading;
  const patientsLoading = patientApiResult.isLoading;
  const categoriesLoading = categoryApiResult.isLoading;
  const subCategoriesLoading = subCategoryApiResult.isLoading;

  // Process dropdown data following standard patterns
  const dropdownData = {
    doctors: (doctorsData || [])
      .filter(user => {
        // More flexible doctor filtering - check multiple possible role indicators
        const role = user.role;
        if (!role) {
          // Check if name contains "Dr" for users without role
          const nameContainsDoctor = (user.firstName + ' ' + user.lastName).toLowerCase().includes('dr');
          return nameContainsDoctor;
        }
        
        // Check the correct role field - it's role.role, not role.roleName!
        const roleName = role.role || role.roleName || role.title || role.name || role.type || '';
        const roleNameLower = roleName.toLowerCase();
        
        // Check if it contains doctor-related keywords
        const isDoctorRole = roleNameLower.includes('doctor') || 
                           roleNameLower.includes('physician') || 
                           roleNameLower.includes('dr') ||
                           roleNameLower === 'doctor' ||
                           user.isDoctor === true ||
                           user.userType === 'doctor';
        
        return isDoctorRole;
      })
      .map(doctor => ({
        label: `${doctor.firstName || ''} ${doctor.lastName || ''}`.trim() || doctor.email || 'Unknown Doctor',
        value: doctor._id
      })),
    
    items: (itemsData || []).map(item => {
      // Handle category and subCategory - can be ObjectId string or populated object
      const categoryId = item.category 
        ? (typeof item.category === 'object' ? item.category._id : item.category)
        : null;
      const subCategoryId = item.subCategory 
        ? (typeof item.subCategory === 'object' ? item.subCategory._id : item.subCategory)
        : null;
      
      return {
        label: item.itemName || 'Unnamed Item',
        value: item._id,
        categoryId: categoryId,
        subCategoryId: subCategoryId
      };
    }),
    
    units: (unitsData || []).map(unit => ({
      label: unit.unitName || 'Unnamed Unit',
      value: unit._id
    })),
    
    patients: (patientsData || []).map(patient => ({
      label: patient.UHID || patient.uhid || patient._id || 'No UHID',
      value: patient.UHID || patient.uhid || patient._id,
      fullName: `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || 'Unknown Patient'
    })),
    
    categories: (categoriesData || []).map(category => ({
      label: category.categoryName || 'Unnamed Category',
      value: category._id
    })),
    
    subCategories: (subCategoriesData || []).map(subCategory => {
      // Handle parentCategory - can be ObjectId string or populated object
      const parentCategoryId = subCategory.parentCategory 
        ? (typeof subCategory.parentCategory === 'object' 
            ? subCategory.parentCategory._id 
            : subCategory.parentCategory)
        : null;
      
      return {
        label: subCategory.subCategoryName || subCategory.categoryName || 'Unnamed SubCategory',
        value: subCategory._id,
        categoryId: parentCategoryId
      };
    })
  };

  // Handle form input changes
  const handleInputChange = (name, value) => {
    // If category changes, reset subcategory
    if (name === 'category') {
      setFormData(prev => ({
        ...prev,
        category: value,
        subCategory: null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields and collect error field names
    const errorFields = [];
    if (!formData.startDate) {
      errorFields.push('startDate');
    }
    if (!formData.endDate) {
      errorFields.push('endDate');
    }
    
    if (errorFields.length > 0) {
      setErrors(errorFields);
      toast.error('Please select both start and end dates');
      return;
    }
    
    // Clear errors if validation passes
    setErrors([]);

    try {
      setLoading(true);
      setError(null);
      
      // Prepare params for API call - Simplified date handling
      let startDateFormatted, endDateFormatted;
      
      try {
        // Use date-fns consistently for all date formatting
        startDateFormatted = formData.startDate ? format(formData.startDate, 'yyyy-MM-dd') : null;
        endDateFormatted = formData.endDate ? format(formData.endDate, 'yyyy-MM-dd') : null;
      } catch (dateError) {
        console.error("Date formatting error:", dateError);
        toast.error('Error formatting dates. Please check your date selections.');
        return;
      }

      const params = {
        ...formData,
        startDate: startDateFormatted,
        endDate: endDateFormatted,
        page: pagination.page + 1, // API is 1-based, MUI is 0-based
        limit: pagination.rowsPerPage,
      };

      // Remove null/undefined values
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      // Extract only the values from objects for API call
      const apiParams = {};
      
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (value && typeof value === 'object' && value.value !== undefined) {
          // Extract only the value from dropdown objects
          apiParams[key] = value.value;
        } else {
          // Keep primitive values as is
          apiParams[key] = value;
        }
      });

      const response = await getExpenseReportApi(apiParams);
      
      // Handle response following standard pattern
      if (response.isSuccess) {
        const responseData = response.data || {};
        
        setReportData(responseData.data || []);
        setPagination(prev => ({
          ...prev,
          total: responseData.pagination?.total || 0,
          totalPages: responseData.pagination?.pages || 1,
        }));
        
        if ((responseData.data || []).length === 0) {
          toast.info('No data found for the selected criteria');
        }
      } else {
        throw new Error(response.message || 'Failed to fetch expense report');
      }
    } catch (err) {
      console.error('Error fetching expense report:', err);
      const errorMessage = err.message || 'An error occurred while fetching the report';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle export functionality
  const handleExport = async () => {
    try {
      setLoading(true);
      
      // Prepare params for export - matching fetchReportData params
      const exportParams = {
        ...formData,
        startDate: formData.startDate ? format(formData.startDate, 'yyyy-MM-dd') : null,
        endDate: formData.endDate ? format(formData.endDate, 'yyyy-MM-dd') : null,
        page: 1,
        limit: 100000, // Export all records
      };

      // Remove null/undefined values
      Object.keys(exportParams).forEach(key => {
        if (exportParams[key] === null || exportParams[key] === undefined || exportParams[key] === '') {
          delete exportParams[key];
        }
      });

      // Extract values from dropdown objects
      const apiParams = {};
      Object.keys(exportParams).forEach(key => {
        const value = exportParams[key];
        if (value && typeof value === 'object' && value.value !== undefined) {
          apiParams[key] = value.value;
        } else {
          apiParams[key] = value;
        }
      });

      // Define column order for Excel export (matching table header order)
      const columnOrder = ['uhid', 'patientName', 'visitDate', 'visitType', 'itemName', 'quantity', 'amount'];

      // Use the standard export function with proper Excel formatting and column spacing
      const result = await exportExpenseReportApi(apiParams, columnOrder);
      
      if (result.success) {
        toast.success(`Successfully exported ${result.count} records to Excel!`);
      } else {
        throw new Error('Export failed');
      }
    } catch (err) {
      console.error('Export error:', err);
      const errorMessage = err.message || 'Failed to export report';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle PDF export
  const handlePDFExport = async () => {
    try {
      setLoading(true);
      
      // Prepare export parameters - matching fetchReportData params
      const exportParams = {
        ...formData,
        startDate: formData.startDate ? format(formData.startDate, 'yyyy-MM-dd') : null,
        endDate: formData.endDate ? format(formData.endDate, 'yyyy-MM-dd') : null,
        page: 1,
        limit: 100000, // Export all records for PDF
      };

      // Remove null/undefined values
      Object.keys(exportParams).forEach(key => {
        if (exportParams[key] === null || exportParams[key] === undefined || exportParams[key] === '') {
          delete exportParams[key];
        }
      });

      // Extract values from dropdown objects
      const apiParams = {};
      Object.keys(exportParams).forEach(key => {
        const value = exportParams[key];
        if (value && typeof value === 'object' && value.value !== undefined) {
          apiParams[key] = value.value;
        } else {
          apiParams[key] = value;
        }
      });

      // Define columns for PDF export with proper labels (matching table header order and visible columns)
      const allColumns = [
        { header: 'UHID', dataKey: 'uhid', visibleKey: 'uhid' },
        { header: 'Patient Name', dataKey: 'patientName', visibleKey: 'patientName' },
        { header: 'Visit Date', dataKey: 'visitDate', visibleKey: 'visitDate' },
        { header: 'Visit Type', dataKey: 'visitType', visibleKey: 'visitType' },
        { header: 'Item/Service', dataKey: 'itemService', visibleKey: 'itemService' },
        { header: 'Quantity', dataKey: 'quantity', visibleKey: 'quantity' },
        { header: 'Amount', dataKey: 'amount', visibleKey: 'amount' },
      ];
      
      // Filter columns based on visibleColumns state
      const columns = allColumns.filter(col => visibleColumns[col.visibleKey]).map(({ visibleKey, ...col }) => col);

      // Prepare date range for PDF header
      const dateRange = formData.startDate && formData.endDate ? {
        startDate: formData.startDate.toISOString(),
        endDate: formData.endDate.toISOString(),
      } : null;

      const result = await exportExpenseReportPDF(apiParams, columns, dateRange);
      
      if (result.success) {
        toast.success(`Successfully exported ${result.count} records to PDF!`);
      } else {
        throw new Error('PDF export failed');
      }
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(`PDF export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination
  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
    
    // Re-fetch data for the new page
    const fetchPageData = async () => {
      if (!formData.startDate || !formData.endDate) {
        return; // Don't fetch if required fields are missing
      }

      try {
        setLoading(true);
        setError(null);
        
        // Handle date formatting for pagination
        const startDateFormatted = formData.startDate ? format(formData.startDate, 'yyyy-MM-dd') : null;
        const endDateFormatted = formData.endDate ? format(formData.endDate, 'yyyy-MM-dd') : null;
        
        // Prepare params for API call
        const params = {
          ...formData,
          startDate: startDateFormatted,
          endDate: endDateFormatted,
          page: newPage + 1, // API is 1-based, MUI is 0-based
          limit: pagination.rowsPerPage,
        };

        // Remove null/undefined values and extract values from objects
        const apiParams = {};
        Object.keys(params).forEach(key => {
          const value = params[key];
          if (value !== null && value !== undefined && value !== '') {
            if (value && typeof value === 'object' && value.value !== undefined) {
              apiParams[key] = value.value;
            } else {
              apiParams[key] = value;
            }
          }
        });

        // Fetch report data
        const response = await getExpenseReportApi(apiParams);
        
        if (response.isSuccess) {
          const responseData = response.data || {};
          setReportData(responseData.data || []);
          setPagination(prev => ({
            ...prev,
            total: responseData.pagination?.total || 0,
          }));
        } else {
          throw new Error(response.message || 'Failed to fetch report data');
        }
      } catch (err) {
        console.error('Error fetching report:', err);
        const errorMessage = err.message || 'Failed to fetch report data';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPagination({
      page: 0,
      rowsPerPage: newRowsPerPage,
      total: pagination.total,
    });
    
    // Re-fetch data with new page size
    if (reportData.length > 0) {
      const fetchPageData = async () => {
        try {
          setLoading(true);
          setError(null);
          
          // Handle date formatting for pagination
          const startDateFormatted = formData.startDate ? format(formData.startDate, 'yyyy-MM-dd') : null;
          const endDateFormatted = formData.endDate ? format(formData.endDate, 'yyyy-MM-dd') : null;
          
          // Prepare params for API call
          const params = {
            ...formData,
            startDate: startDateFormatted,
            endDate: endDateFormatted,
            page: 1, // Reset to first page
            limit: newRowsPerPage,
          };

          // Remove null/undefined values and extract values from objects
          const apiParams = {};
          Object.keys(params).forEach(key => {
            const value = params[key];
            if (value !== null && value !== undefined && value !== '') {
              if (value && typeof value === 'object' && value.value !== undefined) {
                apiParams[key] = value.value;
              } else {
                apiParams[key] = value;
              }
            }
          });

          // Fetch report data
          const response = await getExpenseReportApi(apiParams);
          
          if (response.isSuccess) {
            const responseData = response.data || {};
            setReportData(responseData.data || []);
            setPagination(prev => ({
              ...prev,
              total: responseData.pagination?.total || 0,
            }));
          } else {
            throw new Error(response.message || 'Failed to fetch report data');
          }
        } catch (err) {
          console.error('Error fetching report:', err);
          const errorMessage = err.message || 'Failed to fetch report data';
          setError(errorMessage);
          toast.error(errorMessage);
        } finally {
          setLoading(false);
        }
      };

      fetchPageData();
    }
  };

  const handleBack = () => {
    navigate(-1); // Go back to the previous page
  };

  // Handle filter icon click (column visibility)
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Handle share functionality
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Patient Wise Item Wise Expense Report',
        text: `Patient Wise Item Wise Expense Report - ${pagination.total || 0} records`,
        url: window.location.href,
      }).catch((error) => {
        console.log('Error sharing:', error);
      });
    } else {
      // Fallback: Copy URL to clipboard
      navigator.clipboard.writeText(window.location.href).then(() => {
        toast.success('Report URL copied to clipboard');
      }).catch(() => {
        toast.error('Failed to copy URL');
      });
    }
  };

  // Handle download menu
  const handleDownloadClick = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  // Handle menu item clicks
  const handleMenuClick = (item) => {
    handleDownloadClose();
    switch (item) {
      case 'Excel':
        handleExport();
        break;
      case 'PDF':
        handlePDFExport();
        break;
      case 'Report Mail Send Scheduler':
        // TODO: Implement report mail scheduler when feature is available
        toast.info('Report Mail Send Scheduler feature coming soon');
        break;
      case 'Report Scheduler':
        // TODO: Implement report scheduler when feature is available
        toast.info('Report Scheduler feature coming soon');
        break;
      default:
        break;
    }
  };
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth={false} disableGutters>
        <Header />
        <Box sx={{ display: "flex" }}>
          <Box>
            <Sidebar />
          </Box>
          <Box sx={{ width: "100%", pr: "90px" }}>
            <Box sx={{ my: "20px" }}>
              <Button
                startIcon={<ArrowLeft />}
                onClick={handleBack}
                sx={{
                  color: "#5D6A82",
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: "bold",
                  padding: 0,
                  transition: "none !important",
                  marginBottom: "16px",
                }}
              >
                Back
              </Button>
              <Typography sx={{
                fontFamily: "Open Sans",
                fontWeight: 600,
                fontSize: "20px",
                lineHeight: "29px",
                letterSpacing: "0px",
                color: "#5D6A82",
                marginBottom: "1rem",
              }}>
                Patient Wise item Wise Expense Report
              </Typography>
            </Box>
            <Box>
              <Grid spacing={2} container>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={templateOptions}
                    getOptionLabel={(option) => option?.label || ""}
                    value={formData.searchTemplate || null}
                    onChange={(e, value) => handleInputChange('searchTemplate', value)}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Template"
                        name="searchTemplate"
                        fullWidth
                      />
                    )}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        name="startDate"
                        label="Start Date"
                        value={formData.startDate}
                        onChange={(newValue) => {
                          handleInputChange('startDate', newValue);
                          // Clear error when field is changed
                          if (errors.includes('startDate')) {
                            setErrors(errors.filter(e => e !== 'startDate'));
                          }
                        }}
                        slotProps={{
                          textField: { 
                            required: true, 
                            fullWidth: true,
                            InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                          },
                        }}
                        sx={{ 
                          bgcolor: "white",
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              border: errors?.includes('startDate') ? '1px solid red' : '1px solid inherit',
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        name="endDate"
                        label="End Date"
                        value={formData.endDate}
                        onChange={(newValue) => {
                          handleInputChange('endDate', newValue);
                          // Clear error when field is changed
                          if (errors.includes('endDate')) {
                            setErrors(errors.filter(e => e !== 'endDate'));
                          }
                        }}
                        slotProps={{
                          textField: { 
                            required: true, 
                            fullWidth: true,
                            InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                          },
                        }}
                        sx={{ 
                          bgcolor: "white",
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              border: errors?.includes('endDate') ? '1px solid red' : '1px solid inherit',
                            },
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={dropdownData.categories}
                    getOptionLabel={(option) => option?.label || ''}
                    value={formData.category || null}
                    onChange={(e, value) => handleInputChange('category', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Category"
                        name="category"
                        fullWidth
                      />
                    )}
                    loading={doctorsLoading || itemsLoading || unitsLoading || 
                      patientsLoading || categoriesLoading || subCategoriesLoading}
                    noOptionsText="No categories found"
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={formData.category 
                      ? dropdownData.subCategories.filter(
                          sub => sub.categoryId === formData.category?.value
                        ) 
                      : []}
                    getOptionLabel={(option) => option?.label || ''}
                    value={formData.subCategory || null}
                    onChange={(e, value) => handleInputChange('subCategory', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sub Category"
                        name="subCategory"
                        fullWidth
                      />
                    )}
                    disabled={!formData.category}
                    loading={doctorsLoading || itemsLoading || unitsLoading || 
                      patientsLoading || categoriesLoading || subCategoriesLoading}
                    noOptionsText={formData.category ? 'No subcategories found' : 'Select a category first'}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={Array.isArray(dropdownData?.patients) ? dropdownData.patients : []}
                    getOptionLabel={(option) => option?.value || ''}
                    value={formData.patientUhid ? 
                      (dropdownData?.patients || []).find(p => p?.value === formData.patientUhid) || null 
                      : null
                    }
                    onChange={(e, value) => {
                      handleInputChange('patientUhid', value?.value || '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Patient UHID"
                        name="patientUhid"
                        fullWidth
                      />
                    )}
                    loading={doctorsLoading || itemsLoading || unitsLoading || 
                      patientsLoading || categoriesLoading || subCategoriesLoading}
                    noOptionsText={doctorsLoading || itemsLoading || unitsLoading || 
                      patientsLoading || categoriesLoading || subCategoriesLoading ? 'Loading...' : 'No patients found'}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    sx={{ bgcolor: "white" }}
                  />
                  {(!dropdownData?.patients?.length && !doctorsLoading && !itemsLoading && !unitsLoading && !patientsLoading && !categoriesLoading && !subCategoriesLoading) && (
                    <Typography variant="caption" color="textSecondary">
                      No patients available
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={
                      Array.isArray(dropdownData?.items) 
                        ? dropdownData.items.filter(item => {
                            // Filter items based on selected category and subcategory
                            if (formData?.category && formData?.subCategory) {
                              return item.categoryId === formData.category.value && 
                                     item.subCategoryId === formData.subCategory.value;
                            } else if (formData?.category) {
                              return item.categoryId === formData.category.value;
                            }
                            return true; // Show all items if no filters
                          })
                        : []
                    }
                    getOptionLabel={(option) => option?.label || ''}
                    value={formData.item}
                    onChange={(e, value) => handleInputChange('item', value)}
                    renderInput={(params) => (
                      <TextField {...params} label="Item" name="item" fullWidth />
                    )}
                    loading={doctorsLoading || itemsLoading || unitsLoading || 
                      patientsLoading || categoriesLoading || subCategoriesLoading}
                    noOptionsText="No items found"
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={visitTypeOptions}
                    getOptionLabel={(option) => option?.label || ""}
                    value={formData.visitType || null}
                    onChange={(e, value) => handleInputChange('visitType', value)}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Visit Type"
                        name="visitType"
                        fullWidth
                      />
                    )}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={dropdownData.doctors}
                    getOptionLabel={(option) => option?.label || ""}
                    value={formData.doctor || null}
                    onChange={(e, value) => {
                      handleInputChange('doctor', value);
                    }}
                    onOpen={() => {
                      // Dropdown opened
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Doctor"
                        name="doctor"
                        fullWidth
                      />
                    )}
                    loading={doctorsLoading || itemsLoading || unitsLoading || 
                      patientsLoading || categoriesLoading || subCategoriesLoading}
                    noOptionsText="No doctors found"
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={expenseTypeOptions}
                    getOptionLabel={(option) => option?.label || ""}
                    value={formData.expenseType || null}
                    onChange={(e, value) => handleInputChange('expenseType', value)}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Expense Type"
                        name="expenseType"
                        fullWidth
                      />
                    )}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={billStatusOptions}
                    getOptionLabel={(option) => option?.label || ""}
                    value={formData.billStatus || null}
                    onChange={(e, value) => handleInputChange('billStatus', value)}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Bill Status"
                        name="billStatus"
                        fullWidth
                      />
                    )}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={expenseStatusOptions}
                    getOptionLabel={(option) => option?.label || ""}
                    value={formData.expenseStatus || null}
                    onChange={(e, value) => handleInputChange('expenseStatus', value)}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Expense Status"
                        name="expenseStatus"
                        fullWidth
                      />
                    )}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={dropdownData.units}
                    getOptionLabel={(option) => option?.label || ''}
                    value={formData.unit || null}
                    onChange={(e, value) => handleInputChange('unit', value)}
                    renderInput={(params) => (
                      <TextField {...params} label="Unit" name="unit" fullWidth />
                    )}
                    loading={doctorsLoading || itemsLoading || unitsLoading || 
                      patientsLoading || categoriesLoading || subCategoriesLoading}
                    noOptionsText="No units found"
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: "20px",
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                onClick={handleExport}
                disabled={loading}
                sx={{ 
                  minWidth: 120,
                  textTransform: "none",
                }}
              >
                {loading ? <CircularProgress size={20} /> : 'EXPORT'}
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  backgroundColor: "rgba(1, 121, 176, 1)",
                  width: "150px",
                }}
              >
                {loading ? <CircularProgress size={20} /> : 'Search'}
              </Button>
            </Box>

            {/* Search Results Table */}
            <Box
              sx={{
                border: "1px solid rgba(223, 232, 239, 1)",
                borderRadius: "8px",
                my: "20px",
                p: "20px",
              }}
            >
                {/* Header with Rows/Columns count and action icons */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 1,
                    pb: "15px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "14px", color: "rgba(93, 106, 130, 1)" }}
                  >
                    Rows: {Array.isArray(reportData) ? reportData.length : 0} | Columns:{" "}
                    {Object.values(visibleColumns).filter(v => v).length}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box sx={iconStyleProps} onClick={handleFilterClick}>
                      <EditIcon
                        sx={{
                          cursor: "pointer",
                          color: "transparent",
                          fontSize: "18px",
                        }}
                      />
                    </Box>
                    <Box sx={iconStyleProps} onClick={handleShare}>
                      <ShareIcon 
                        sx={{
                          cursor: "pointer",
                          fontSize: "18px",
                        }}
                      />
                    </Box>
                    <Box sx={iconStyleProps}>
                      <IconButton onClick={handleDownloadClick}>
                        <FileDownloadOutlinedIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
                
                <TableContainer component={Paper}>
                  <Table aria-label="expense report table" sx={{ minWidth: 800 }}>
                    <TableHead
                      sx={{
                        backgroundColor: "#f5f5f5",
                        "& th": { fontWeight: "bold" },
                      }}
                    >
                      <TableRow>
                        {visibleColumns.uhid && <TableCell>UHID</TableCell>}
                        {visibleColumns.patientName && <TableCell>Patient Name</TableCell>}
                        {visibleColumns.visitDate && <TableCell>Visit Date</TableCell>}
                        {visibleColumns.visitType && <TableCell>Visit Type</TableCell>}
                        {visibleColumns.itemService && <TableCell>Item/Service</TableCell>}
                        {visibleColumns.quantity && <TableCell>Quantity</TableCell>}
                        {visibleColumns.amount && <TableCell>Amount</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress />
                            <Typography sx={{ mt: 2 }}>Loading report data...</Typography>
                          </TableCell>
                        </TableRow>
                      ) : !Array.isArray(reportData) || reportData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="textSecondary">No data available. Please search with filters.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        reportData.map((row, index) => {
                          // Helper function to safely format dates
                          const formatDate = (dateValue) => {
                            if (!dateValue) {
                              return '-';
                            }
                            
                            try {
                              const date = new Date(dateValue);
                              
                              if (isNaN(date.getTime())) {
                                return '-';
                              }
                              
                              return date.toLocaleDateString('en-GB');
                            } catch (error) {
                              console.error('Date formatting error:', error);
                              return '-';
                            }
                          };

                          // Helper function to safely format currency
                          const formatCurrency = (amount) => {
                            if (!amount && amount !== 0) return '₹0';
                            const numAmount = Number(amount);
                            if (isNaN(numAmount)) return '₹0';
                            return `₹${numAmount.toLocaleString()}`;
                          };

                          return (
                            <TableRow key={row._id || index}>
                              {visibleColumns.uhid && <TableCell>{row.uhid || row.patientUHID || row.UHID || '-'}</TableCell>}
                              {visibleColumns.patientName && <TableCell>
                                {row.patientName || 
                                 `${row.firstName || ''} ${row.lastName || ''}`.trim() || 
                                 row.patient?.name || 
                                 '-'}
                              </TableCell>}
                              {visibleColumns.visitDate && <TableCell>
                                {formatDate(row.consultationDateTime || row.visitDate || row.createdAt)}
                              </TableCell>}
                              {visibleColumns.visitType && <TableCell>
                                {row.visitType || '-'}
                              </TableCell>}
                              {visibleColumns.itemService && <TableCell>
                                {row.itemName || row.serviceName || '-'}
                              </TableCell>}
                              {visibleColumns.quantity && <TableCell>
                                {row.quantity || '-'}
                              </TableCell>}
                              {visibleColumns.amount && <TableCell>
                                {formatCurrency(row.amount || row.totalAmount || row.total)}
                              </TableCell>}
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                {pagination.total > 0 && (
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={pagination.total}
                    rowsPerPage={pagination.rowsPerPage}
                    page={pagination.page}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                )}
                
                {/* Filter Menu */}
                <Menu
                  anchorEl={filterAnchorEl}
                  open={filterMenuOpen}
                  onClose={handleFilterClose}
                  PaperProps={{
                    style: {
                      maxHeight: 400,
                      padding: "8px 0",
                      width: "200px",
                    },
                  }}
                >
                  <MenuItem sx={{ ...menuItemStyle, fontWeight: "bold" }} disabled>
                    Show/Hide Columns
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => toggleColumn('uhid')} sx={menuItemStyle}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ flex: 1 }}>UHID</Box>
                      {visibleColumns.uhid && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                    </Box>
                  </MenuItem>
                  <MenuItem onClick={() => toggleColumn('patientName')} sx={menuItemStyle}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ flex: 1 }}>Patient Name</Box>
                      {visibleColumns.patientName && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                    </Box>
                  </MenuItem>
                  <MenuItem onClick={() => toggleColumn('visitDate')} sx={menuItemStyle}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ flex: 1 }}>Visit Date</Box>
                      {visibleColumns.visitDate && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                    </Box>
                  </MenuItem>
                  <MenuItem onClick={() => toggleColumn('visitType')} sx={menuItemStyle}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ flex: 1 }}>Visit Type</Box>
                      {visibleColumns.visitType && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                    </Box>
                  </MenuItem>
                  <MenuItem onClick={() => toggleColumn('itemService')} sx={menuItemStyle}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ flex: 1 }}>Item/Service</Box>
                      {visibleColumns.itemService && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                    </Box>
                  </MenuItem>
                  <MenuItem onClick={() => toggleColumn('quantity')} sx={menuItemStyle}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ flex: 1 }}>Quantity</Box>
                      {visibleColumns.quantity && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                    </Box>
                  </MenuItem>
                  <MenuItem onClick={() => toggleColumn('amount')} sx={menuItemStyle}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ flex: 1 }}>Amount</Box>
                      {visibleColumns.amount && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                    </Box>
                  </MenuItem>
                </Menu>
                
                {/* Download Menu */}
                <Menu
                  anchorEl={downloadAnchorEl}
                  open={downloadMenuOpen}
                  onClose={handleDownloadClose}
                  slotProps={{
                    sx: {
                      borderRadius: "12px",
                      boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                      padding: "8px 0",
                      width: "250px",
                    },
                  }}
                >
                  {menuItems.map((item, index) => (
                    <React.Fragment key={item}>
                      <MenuItem onClick={() => handleMenuClick(item)} sx={menuItemStyle}>
                        {item}
                      </MenuItem>
                      {index !== menuItems.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </Menu>
              </Box>

            {/* Error Display */}
            {error && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error">
                  {error}
                </Alert>
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </LocalizationProvider>
  );
}
